import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  ButtonGroup, 
  Popover, 
  Typography, 
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip
} from '@mui/material';
import { 
  Palette as PaletteIcon,
  GridOn as GridIcon,
  EditSquare as EditIcon,
  ScatterPlot as StitchIcon,
  MoreVert as MoreIcon,
  FileDownload,
  Image as ImageIcon,
  Upload,
  PhotoLibrary,
  Settings as SettingsIcon,
  Undo,
  Redo,
  Polyline as ShapesIcon
} from '@mui/icons-material';
import ColorPickerPanel from './ColorPickerPanel';
import StitchPickerPanel from './StitchPickerPanel';
import GridPanel from './GridPanel';
import EditPanel from './EditPanel';
import ShapesPanel from './ShapesPanel';
import ImageUploader from './ImageUploader';
import MotifBrowser from '../../../Motif/MotifBrowser';
import { useChartData } from './ChartDataProvider';

export default function TopMenuBar({ canvasRef, activePanel, onPanelToggle }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [showMotifBrowser, setShowMotifBrowser] = useState(false);
  
  // Get chart data from context
  const { actions, data, colors, rows, cols, settings, motifForPlacement, canUndo, canRedo } = useChartData();

  const handleMenuClick = (event, menuType) => {
    // Prevent panel switching when motif placement is active
    if (motifForPlacement && (menuType === 'colors' || menuType === 'stitches' || menuType === 'grid' || menuType === 'edit' || menuType === 'shapes' || menuType === 'settings')) {
      return; // Ignore clicks on panel buttons during motif placement
    }
    
    if (menuType === 'options') {
      // Handle options dropdown separately
      setOptionsAnchorEl(event.currentTarget);
      return;
    }
    
    if (menuType === 'colors') {
      // Toggle colors panel
      const newPanel = activePanel === 'colors' ? null : 'colors';
      onPanelToggle(newPanel);
      if (newPanel === 'colors') {
        // Enable color mode when colors panel is opened
        actions.setColorMode(true);
        // Disable other modes when colors panel is opened
        actions.setStitchMode(false);
        actions.setShapeMode(false);
      } else {
        // Disable color mode when colors panel is closed
        actions.setColorMode(false);
      }
      return;
    }
    
    if (menuType === 'stitches') {
      // Toggle stitches panel
      const newPanel = activePanel === 'stitches' ? null : 'stitches';
      onPanelToggle(newPanel);
      if (newPanel === 'stitches') {
        // Enable stitch mode when stitches panel is opened
        actions.setStitchMode(true);
        // Disable other modes when stitches panel is opened
        actions.setColorMode(false);
        actions.setShapeMode(false);
      } else {
        // Disable stitch mode when stitches panel is closed
        actions.setStitchMode(false);
      }
      return;
    }
    
    if (menuType === 'shapes') {
      // Toggle shapes panel
      const newPanel = activePanel === 'shapes' ? null : 'shapes';
      onPanelToggle(newPanel);
      if (newPanel === 'shapes') {
        // Enable shape mode when shapes panel is opened
        actions.setShapeMode(true);
        // Disable other modes when shapes panel is opened
        actions.setStitchMode(false);
        actions.setColorMode(false);
      } else {
        // Disable shape mode when shapes panel is closed
        actions.setShapeMode(false);
      }
      return;
    }
    
    if (menuType === 'grid') {
      // Toggle grid panel
      const newPanel = activePanel === 'grid' ? null : 'grid';
      onPanelToggle(newPanel);
      if (newPanel !== 'stitches') {
        // Disable stitch mode when grid panel is opened
        actions.setStitchMode(false);
      }
      if (newPanel !== 'colors') {
        // Disable color mode when grid panel is opened
        actions.setColorMode(false);
      }
      if (newPanel !== 'shapes') {
        // Disable shape mode when grid panel is opened
        actions.setShapeMode(false);
      }
      return;
    }
    
    if (menuType === 'edit') {
      // Toggle edit panel
      const newPanel = activePanel === 'edit' ? null : 'edit';
      onPanelToggle(newPanel);
      if (newPanel !== 'stitches') {
        // Disable stitch mode when edit panel is opened
        actions.setStitchMode(false);
      }
      if (newPanel !== 'colors') {
        // Disable color mode when edit panel is opened
        actions.setColorMode(false);
      }
      if (newPanel !== 'shapes') {
        // Disable shape mode when edit panel is opened
        actions.setShapeMode(false);
      }
      return;
    }
    
    if (activeMenu === menuType) {
      // Close menu if it's already open
      setAnchorEl(null);
      setActiveMenu(null);
    } else {
      // Open new menu
      setAnchorEl(event.currentTarget);
      setActiveMenu(menuType);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const handleOptionsClose = () => {
    setOptionsAnchorEl(null);
  };

  // Options dropdown actions
  const handleExportImage = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = 'chart.png';
    link.click();
    handleOptionsClose();
  };

  const handleExportData = () => {
    const dataStr = JSON.stringify(data);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'data.json';
    link.click();
    URL.revokeObjectURL(url);
    handleOptionsClose();
  };

  const handleImportData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const loadedData = JSON.parse(e.target.result);
          actions.updateData(loadedData);
        };
        reader.readAsText(file);
      }
    };
    input.click();
    handleOptionsClose();
  };

  const handleImportImage = () => {
    setShowImageUploader(true);
    handleOptionsClose();
  };

  const handleImportMotif = () => {
    setShowMotifBrowser(true);
    handleOptionsClose();
  };

  const handleSelectMotif = (motif) => {
    // Close the motif browser and set motif for placement
    setShowMotifBrowser(false);
    actions.setMotifForPlacement(motif);
  };

  const handleImageConvert = async (result, conversionMode) => {
    try {
      // Update chart data
      actions.updateData(result.data);
      
      // Update colors if new colors were added
      if (conversionMode === 'new' && result.colors.length > colors.length) {
        actions.updateColors(result.colors);
      }
      
      // Close dialog after conversion
      setShowImageUploader(false);
      
    } catch (error) {
      console.error('Error applying converted image:', error);
    }
  };

  const handleCloseImageUploader = () => {
    setShowImageUploader(false);
  };

  const handleSettingsToggle = () => {
    // Prevent settings toggle when motif placement is active
    if (motifForPlacement) {
      return;
    }
    
    // Toggle settings panel
    const newPanel = activePanel === 'settings' ? null : 'settings';
    onPanelToggle(newPanel);
    if (newPanel === 'settings') {
      // Disable all modes when settings panel is opened
      actions.setStitchMode(false);
      actions.setColorMode(false);
      actions.setShapeMode(false);
    }
  };

  const menuButtons = [
    {
      key: 'colors',
      label: 'Colors',
      icon: <PaletteIcon />,
      component: <ColorPickerPanel />
    },
    {
      key: 'stitches',
      label: 'Stitches',
      icon: <StitchIcon />,
      component: <StitchPickerPanel />
    },
    {
      key: 'shapes',
      label: 'Shapes',
      icon: <ShapesIcon />,
      component: <ShapesPanel />
    },
    {
      key: 'grid',
      label: 'Grid',
      icon: <GridIcon />,
      component: <GridPanel />
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIcon />,
      component: <EditPanel />
    }
  ];

  return (
    <Box>
      {/* Menu Bar */}
      <Box sx={{ 
        display: 'flex', 
        gap: 1, 
        mb: 2
      }}>
        <ButtonGroup variant="outlined" size="small">
          {menuButtons.map((menu) => (
            <Tooltip 
              key={menu.key} 
              title={motifForPlacement ? "Finish placing motif first" : menu.label} 
              placement="bottom"
            >
              <span>
                <Button
                  onClick={(event) => handleMenuClick(event, menu.key)}
                  disabled={motifForPlacement}
                  variant={
                    menu.key === 'colors' 
                      ? (activePanel === 'colors' ? 'contained' : 'outlined')
                      : menu.key === 'stitches'
                      ? (activePanel === 'stitches' ? 'contained' : 'outlined')
                      : menu.key === 'shapes'
                      ? (activePanel === 'shapes' ? 'contained' : 'outlined')
                      : menu.key === 'grid'
                      ? (activePanel === 'grid' ? 'contained' : 'outlined')
                      : menu.key === 'edit'
                      ? (activePanel === 'edit' ? 'contained' : 'outlined')
                      : (activeMenu === menu.key ? 'contained' : 'outlined')
                  }
                  sx={{ minWidth: '40px', px: 1 }}
                >
                  {menu.icon}
                </Button>
              </span>
            </Tooltip>
          ))}
          
          {/* Settings Button - Between Colorways and More */}
          <Tooltip 
            title={motifForPlacement ? "Finish placing motif first" : "Settings"} 
            placement="bottom"
          >
            <span>
              <Button
                variant={activePanel === 'settings' ? 'contained' : 'outlined'}
                onClick={handleSettingsToggle}
                disabled={motifForPlacement}
                sx={{ minWidth: '40px', px: 1 }}
              >
                <SettingsIcon />
              </Button>
            </span>
          </Tooltip>
          
          {/* More Button (formerly Options) with Dropdown */}
          <Tooltip 
            title={motifForPlacement ? "Finish placing motif first" : "More Options"} 
            placement="bottom"
          >
            <span>
              <Button
                onClick={(event) => handleMenuClick(event, 'options')}
                variant="outlined"
                disabled={motifForPlacement}
                sx={{ minWidth: '40px', px: 1 }}
              >
                <MoreIcon />
              </Button>
            </span>
          </Tooltip>
        </ButtonGroup>
        
        {/* Undo/Redo Button Group */}
        <Box>
          <Tooltip title="Undo" placement="bottom">
            <span>
              <Button
                onClick={actions.undo}
                disabled={!canUndo || motifForPlacement}
                sx={{ minWidth: '40px', px: 1 }}
              >
                <Undo />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Redo" placement="bottom">
            <span>
              <Button
                onClick={actions.redo}
                disabled={!canRedo || motifForPlacement}
                sx={{ minWidth: '40px', px: 1 }}
              >
                <Redo />
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Popover Menu */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: '300px',
            maxWidth: '500px'
          }
        }}
      >
        <Paper sx={{ p: 2 }}>
          {activeMenu && (
            <>
              <Typography variant="h6" sx={{ mb: 2, textTransform: 'capitalize' }}>
                {activeMenu === 'colors' ? 'Colors' :
                 activeMenu === 'stitches' ? 'Stitches' :
                 activeMenu === 'shapes' ? 'Shapes' :
                 activeMenu === 'grid' ? 'Grid' :
                 activeMenu === 'edit' ? 'Edit' : activeMenu}
              </Typography>
              {menuButtons.find(menu => menu.key === activeMenu)?.component}
            </>
          )}
        </Paper>
      </Popover>

      {/* Options Dropdown Menu */}
      <Menu
        anchorEl={optionsAnchorEl}
        open={Boolean(optionsAnchorEl)}
        onClose={handleOptionsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportImage}>
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as Image</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportData}>
          <ListItemIcon>
            <FileDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Data</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleImportData}>
          <ListItemIcon>
            <Upload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Import Data</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleImportImage}>
          <ListItemIcon>
            <PhotoLibrary fontSize="small" />
          </ListItemIcon>
          <ListItemText>Import Image</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleImportMotif}>
          <ListItemIcon>
            <Upload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Import Motif</ListItemText>
        </MenuItem>
      </Menu>

      {/* Image Upload Dialog */}
      <Dialog open={showImageUploader} onClose={handleCloseImageUploader} maxWidth="md" fullWidth>
        <DialogTitle>Import Image to Chart</DialogTitle>
        <DialogContent>
          <ImageUploader
            onConvert={handleImageConvert}
            onClose={handleCloseImageUploader}
            rows={rows}
            cols={cols}
            cellSize={settings.cellSize}
            colors={colors}
          />
        </DialogContent>
      </Dialog>

      {/* Motif Browser Dialog */}
      <Dialog open={showMotifBrowser} onClose={() => setShowMotifBrowser(false)} fullWidth maxWidth="md">
        <DialogTitle>Import Motif</DialogTitle>
        <DialogContent>
          <MotifBrowser onSelectMotif={handleSelectMotif} onClose={() => setShowMotifBrowser(false)} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
